/**
 * Sentry bootstrap for the PUBLIC booking SPA (audit finding N5).
 *
 * ── DRIFT WARNING ───────────────────────────────────────────────────────
 * This is a deliberate copy of `packages/admin-ui/src/sentry.ts`. The two
 * are ONE policy and must be changed together.
 *
 * Why copied rather than imported: `@workspace/admin-ui` pulls in MUI,
 * Emotion, `@workspace/crypto` and `@workspace/consultation-forms`. This is
 * the one patient-facing page in the system, loaded on a phone over mobile
 * data by someone trying to book a pharmacy appointment, and it currently
 * depends on no workspace package at all. Importing the admin barrel to
 * reach one function would be a large bundle regression on exactly the
 * surface where page weight matters most, and MUI/Emotion's side effects
 * make tree-shaking it back out unreliable. The proper fix is a small
 * `@workspace/observability` package that both sides import; that is a
 * structural change and is recorded as follow-up rather than smuggled in
 * with a security fix.
 * ────────────────────────────────────────────────────────────────────────
 *
 * This surface is not "less sensitive" than the admin panels for being
 * public — it is where the patient TYPES their name, date of birth and
 * presenting condition. Session Replay records here at the same rates, so
 * the masking policy matters at least as much.
 */
import * as Sentry from "@sentry/react";

const SENSITIVE_PARAMS = [
  "consultationId",
  "bookingId",
  "patientName",
  "patientId",
  "pharmacyId",
  "chainId",
  "uid",
  "email",
  "token",
  "oobCode",
  // Booking-specific: the manage-link bearer token. A cancel/reschedule
  // token in a Sentry URL is a live credential for someone's appointment.
  "manageToken",
];

/**
 * See the note on the same constant in packages/admin-ui/src/sentry.ts:
 * no hyphens (keeps kebab-case routes) and at least one digit (keeps long
 * all-letter route names).
 */
const OPAQUE_SEGMENT = /^(?=.*[0-9])[A-Za-z0-9]{16,}$/;

export function scrubUrl(raw: string): string {
  if (!raw) return raw;

  try {
    const hasScheme = /^[a-z][a-z0-9+.-]*:\/\//i.test(raw);
    // `globalThis.URL` rather than bare `URL`: the booking app's ESLint config
    // declares no browser globals, so a bare reference is a no-undef error
    // there. Kept identical in both copies of this file — see the drift note.
    const url = new globalThis.URL(
      raw,
      hasScheme ? undefined : "https://placeholder.invalid",
    );

    for (const key of SENSITIVE_PARAMS) {
      if (url.searchParams.has(key)) url.searchParams.set(key, "[redacted]");
    }

    url.pathname = url.pathname
      .split("/")
      .map((segment) => (OPAQUE_SEGMENT.test(segment) ? "[id]" : segment))
      .join("/");

    url.hash = "";

    return hasScheme ? url.toString() : `${url.pathname}${url.search}`;
  } catch {
    return raw;
  }
}

/** See the note on the same interface in packages/admin-ui/src/sentry.ts. */
interface ScrubbableEvent {
  request?: {
    url?: string;
    headers?: Record<string, string | undefined>;
  };
  breadcrumbs?: Array<{ data?: Record<string, unknown> }>;
}

export function scrubEventUrls<T extends ScrubbableEvent>(event: T): T {
  if (event.request?.url) {
    event.request.url = scrubUrl(event.request.url);
  }
  if (event.request?.headers?.Referer) {
    event.request.headers.Referer = scrubUrl(event.request.headers.Referer);
  }
  if (event.breadcrumbs) {
    for (const crumb of event.breadcrumbs) {
      if (typeof crumb.data?.url === "string") crumb.data.url = scrubUrl(crumb.data.url);
      if (typeof crumb.data?.from === "string") crumb.data.from = scrubUrl(crumb.data.from);
      if (typeof crumb.data?.to === "string") crumb.data.to = scrubUrl(crumb.data.to);
    }
  }
  return event;
}

export function initBookingSentry(
  env: Record<string, string | undefined | boolean>,
): void {
  const isDev = env.DEV === true || env.DEV === "true";
  const dsn =
    (env.VITE_SENTRY_DSN as string | undefined) ??
    "https://bea64bd8f45e3755b00dbf648311b00e@o4510440882503680.ingest.de.sentry.io/4511734099345488";

  Sentry.init({
    dsn,
    environment: (env.MODE as string | undefined) ?? "production",
    integrations: [
      Sentry.browserTracingIntegration(),
      Sentry.replayIntegration({
        maskAllText: true,
        maskAllInputs: true,
        blockAllMedia: true,
      }),
    ],
    tracesSampleRate: isDev ? 1.0 : 0.1,
    replaysSessionSampleRate: 0.1,
    replaysOnErrorSampleRate: 1.0,
    sendDefaultPii: false,
    beforeSend: scrubEventUrls,
    beforeSendTransaction: scrubEventUrls,
    initialScope: {
      tags: { "app.name": "booking" },
    },
  });
}
